// ============================================================================
// AthleteContext -- Global athlete selector for coaches viewing team members
// ============================================================================

import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/api/client'
import { useAuth } from '@/contexts/AuthContext'
import { loadPersistedSelection, persistSelection } from '@/contexts/athleteSelection'

// ============================================================================
// TYPES
// ============================================================================

export interface ViewableAthlete {
  profile_id: number
  user_id?: number | null
  name: string
  email?: string | null
  group_uuid: string
  group_name: string
}

interface AthleteContextValue {
  /** null = viewing own data ("Myself") */
  selectedProfileId: number | null
  selectedProfileName: string | null
  /** Athletes the coach can view (consent granted) — drives the athlete selector */
  athletes: ViewableAthlete[]
  /** Group members who haven't approved data access yet (consent pending).
   * Shown on the team overview but never selectable for "view-as". */
  pendingAthletes: ViewableAthlete[]
  /** True when groups/profiles are still loading */
  isLoading: boolean
  /** True when the user monitors any group (has viewable or pending athletes) */
  isCoach: boolean
  setSelectedProfile: (profileId: number | null, name: string | null) => void
}

// ============================================================================
// API
// ============================================================================

interface GroupResponse {
  uuid: string
  name: string
  profile_count: number
}

interface ViewableProfileResponse {
  profile_id: number
  user_id?: number | null
  name: string
  email?: string | null
  has_consent: boolean
  joined_at: string
}

function athleteLabel(profile: Pick<ViewableProfileResponse, 'name' | 'email'>) {
  return profile.name?.trim() || profile.email?.trim() || 'Unknown athlete'
}

// ============================================================================
// CONTEXT
// ============================================================================

const AthleteContext = createContext<AthleteContextValue | null>(null)

export function AthleteProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()

  const [selectedProfileId, setProfileId] = useState<number | null>(
    () => loadPersistedSelection().profileId,
  )
  const [selectedProfileName, setProfileName] = useState<string | null>(
    () => loadPersistedSelection().name,
  )

  // Fetch groups the user monitors
  const groupsQuery = useQuery({
    queryKey: ['athlete-context', 'groups'],
    queryFn: async () => {
      const { data } = await apiClient.get<GroupResponse[]>('/groups/')
      return data
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  })

  // Fetch viewable profiles for all groups
  const groups = groupsQuery.data ?? []
  const groupUuids = groups.map((g) => g.uuid).sort().join(',')

  const profilesQuery = useQuery({
    queryKey: ['athlete-context', 'profiles', groupUuids],
    queryFn: async () => {
      const consented: ViewableAthlete[] = []
      const pending: ViewableAthlete[] = []
      // Fetched in parallel, consumed in group order: the output is unchanged.
      const responses = await Promise.all(
        groups.map((group) =>
          apiClient.get<ViewableProfileResponse[]>(`/groups/${group.uuid}/viewable-profiles/`),
        ),
      )
      for (const [index, group] of groups.entries()) {
        for (const profile of responses[index].data) {
          const athlete: ViewableAthlete = {
            profile_id: profile.profile_id,
            user_id: profile.user_id ?? null,
            name: athleteLabel(profile),
            email: profile.email ?? null,
            group_uuid: group.uuid,
            group_name: group.name,
          }
          // Consent granted -> viewable (selectable). Pending -> shown on the
          // team overview only; never offered for "view-as".
          if (profile.has_consent) consented.push(athlete)
          else pending.push(athlete)
        }
      }
      return { consented, pending }
    },
    enabled: isAuthenticated && groups.length > 0,
    staleTime: 5 * 60_000,
  })

  const athletes = profilesQuery.data?.consented ?? []
  const pendingAthletes = profilesQuery.data?.pending ?? []
  const isCoach = athletes.length > 0 || pendingAthletes.length > 0

  // Clear selection if the selected athlete is no longer viewable (consent only)
  useEffect(() => {
    if (!profilesQuery.data || selectedProfileId === null) return
    const stillValid = profilesQuery.data.consented.some((a) => a.profile_id === selectedProfileId)
    if (!stillValid) {
      setProfileId(null)
      setProfileName(null)
      persistSelection(null, null)
    }
  }, [profilesQuery.data, selectedProfileId])

  const setSelectedProfile = useCallback((profileId: number | null, name: string | null) => {
    setProfileId(profileId)
    setProfileName(name)
    persistSelection(profileId, name)
  }, [])

  // The iOS native Lens athlete selector pushes selection changes via a window
  // CustomEvent so the web profile updates in place — no reload, no loader replay.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ id?: number | string | null; name?: string | null }>).detail
      if (!detail) return
      const rawId = detail.id
      const id = rawId == null || rawId === '' ? null : Number(rawId)
      setSelectedProfile(id && !Number.isNaN(id) ? id : null, detail.name ?? null)
    }
    window.addEventListener('beoflow-set-athlete', handler)
    return () => window.removeEventListener('beoflow-set-athlete', handler)
  }, [setSelectedProfile])

  const value = useMemo<AthleteContextValue>(
    () => ({
      selectedProfileId,
      selectedProfileName,
      athletes,
      pendingAthletes,
      isLoading: groupsQuery.isLoading || profilesQuery.isLoading,
      isCoach,
      setSelectedProfile,
    }),
    [selectedProfileId, selectedProfileName, athletes, pendingAthletes, groupsQuery.isLoading, profilesQuery.isLoading, isCoach, setSelectedProfile],
  )

  return <AthleteContext.Provider value={value}>{children}</AthleteContext.Provider>
}

export function useAthlete() {
  const ctx = useContext(AthleteContext)
  if (!ctx) throw new Error('useAthlete must be used within AthleteProvider')
  return ctx
}

/**
 * Non-throwing variant for surfaces that may render OUTSIDE an AthleteProvider,
 * e.g. the public shared-view page. Returns null when there is no provider, so
 * the caller can fall back to "the viewer's own data" (no target_profile_id).
 */
export function useAthleteOptional() {
  return useContext(AthleteContext)
}
